export default function bigTicketCalendar() {
	const root = document.querySelector('[farlo-ui="calendar-big"]');
	if (!root) {
		return;
	}

	// ---------- payload ----------
	let payload;
	try {
		payload = JSON.parse(root.getAttribute('data-cal') || '{}') || {};
	} catch {
		payload = {};
	}

	const specialOfferNote = (payload.specialOfferNote || '').toString().trim();
	const exclusiveOfferNote = (payload.exclusiveOfferNote || '').toString().trim();
	const currency = payload.currency || '£';
	const hideDiscountMessaging = boolish(
		payload?.hideDiscountMessaging ?? payload?.hide_discount_messaging,
		false
	);

	/*
	 * Multi-part show price multiplier.
	 *
	 * Multi-part productions require a separate ticket purchase for each part,
	 * so every displayed price is multiplied by the number of parts.
	 * Kids Week prices are further doubled (one adult + one child per part).
	 *
	 * partsCount is supplied by PHP (olt_calendar_payload_for_show) from the
	 * show_multiple_parts and show_multiple_parts_amount ACF fields.
	 */
	const partsCount = (Number.isInteger(payload.partsCount) && payload.partsCount > 0) ? payload.partsCount : 1;

	const access = payload.campaignAccess || {
		brand: '',
		eligible: false,
		publicOpen: false,
		priorityOpen: false,
		priorityIso: null,
		revealIso: null,
		canBookNow: false,
		finished: false
	};
	const campaignBrandActive = (access.brand && !access.finished);

	// Access performance dates keyed by YYYY-MM-DD, sourced from the access_performances CPT.
	// Each value is an array of type slugs: 'ad', 'cap', 'bsl', 'relaxed'.
	const accessDates = (payload.accessDates && typeof payload.accessDates === 'object')
		? payload.accessDates
		: {};

	// Maps each type slug to its CSS modifier class and accessible label text.
	// Modifier classes correspond to background-image rules in the theme stylesheet.
	const ACCESS_ICON_MAP = {
		ad: { mod: 'ad', label: 'Audio Described' },
		cap: { mod: 'cap', label: 'Captioned' },
		bsl: { mod: 'bsl', label: 'British Sign Language' },
		relaxed: { mod: 'relaxed', label: 'Relaxed' }
	};

	const isCampaignLabel = (l) => /kids/i.test(l) || /new\s*year'?s?\s*sale/i.test(l);
	const visibleLabels = (labels = []) => campaignBrandActive ? labels.slice() : labels.filter((l) => !isCampaignLabel(String(l)));
	function boolish(v, def = true) {
		if (v === undefined || v === null) {
			return def;
		}
		if (typeof v === 'boolean') {
			return v;
		}
		if (typeof v === 'number') {
			return v !== 0;
		}
		const s = String(v).trim().toLowerCase();
		if (['', '0', 'false', 'no', 'off', 'null'].includes(s)) {
			return false;
		}
		if (['1', 'true', 'yes', 'on'].includes(s)) {
			return true;
		}
		return def;
	}
	// Offer = non-OLT, non-campaign. If disabled, only allow today.
	const isOfferAllowed = (p) => {
		if (!disableFutureDeals) {
			return true;
		}
		if (isOlt(p) || isCampaignRow(p)) {
			return true;
		} // only clamp “exclusive/special” items
		return String(p.date) === todayKeyLDN;
	};

	const perfsAll = Array.isArray(payload.performances) ? payload.performances : [];
	const perfs = perfsAll.filter((p) => (p.avail || 0) > 0).sort((a, b) => (a.iso || '').localeCompare(b.iso || ''));

	// ---------- els ----------
	const headerEl = root.querySelector('.farlo-ui-ticket-calendar__header-month-year');
	const daysRoot = root.querySelector('[data-days]');
	const listRoot = root.querySelector('[data-list]');
	const prevBtn = root.querySelector('.farlo-ui-ticket-calendar__button--prev');
	const nextBtn = root.querySelector('.farlo-ui-ticket-calendar__button--next');
	const listToggle = root.querySelector('[data-view-toggle]');
	const filterListEl = root.querySelector('.farlo-ui-ticket-calendar__filter-list');
	const filterTimesPop = root.querySelector('.farlo-ui-ticket-calendar__times');
	const filterTimesInline = root.querySelector('[data-times-inline]');
	const promoWrap = root.querySelector('[data-promos]');

	const isListView = () => {
		const list = root.querySelector('[data-view="list"]');
		return !!(list && !list.hasAttribute('hidden'));
	};

	// ---------- helpers ----------
	const lower = (s) => (s || '').toLowerCase();
	const pad2 = (n) => (n < 10 ? '0' + n : '' + n);
	const parseYMD = (s) => {
		if (!s) {
			return null;
		} const [y, m, d] = s.split('-').map((x) => parseInt(x, 10)); return new Date(y, m - 1, d);
	};
	const dateKey = (y, m, d) => `${ y }-${ pad2(m + 1) }-${ pad2(d) }`;
	const daysInMon = (y, m) => new Date(y, m + 1, 0).getDate();
	const firstDow = (y, m) => {
		const dow = new Date(y, m, 1).getDay();
		return (dow + 6) % 7;
	};
	// A non-positive value means "price unknown", never "free". Return an empty
	// string so callers drop the "from" line instead of printing £0.00.
	const formatMoney = (v) => {
		const n = Number(v);
		return (v === null || !isFinite(n) || n <= 0) ? '' : `${ currency }${ n.toFixed(2) }`;
	};
	const toTimeLabel = (iso) => {
		const dt = new Date(iso); const hh = dt.getHours(),
			mm = dt.getMinutes(); const mml = mm.toString().padStart(2, '0'); const h12 = ((hh + 11) % 12) + 1; const ampm = hh >= 12 ? 'pm' : 'am'; return `${ h12 }.${ mml }${ ampm }`.replace('.00', '');
	};
	const sessionOf = (t) => {
		const [hh, mm] = (t || '').split(':').map((n) => parseInt(n, 10) || 0); return (hh > 17 || (hh === 17 && mm >= 30)) ? 'Evening' : 'Matinee';
	};

	const parseIsoMs = (iso) => {
		const ms = Date.parse(iso || '');
		return Number.isFinite(ms) ? ms : null;
	};

	const LDN_TZ = 'Europe/London';
	const fmtLDN = (d, opts) => d.toLocaleString(undefined, { timeZone: LDN_TZ, ...opts });

	const disableFutureDeals = boolish(
		payload?.showDisableFutureDeals ?? payload?.show_disable_future_deals,
		true
	);
	const todayKeyLDN = (() => {
		const d = new Date();
		const y = fmtLDN(d, { year: 'numeric' });
		const m = fmtLDN(d, { month: '2-digit' });
		const dd = fmtLDN(d, { day: '2-digit' });
		return `${ y }-${ m }-${ dd }`;
	})();

	const revealMs = parseIsoMs(access.revealIso);
	const prioMs = parseIsoMs(access.priorityIso);
	const eligible = access.eligible === true;

	// Check for campaign cookie client-side (bypasses page cache)
	const hasCampaignCookie = () => document.cookie.includes('farlo_campaign_access');

	function getSavingPct(p) {
	// explicit fields first
		const pct = p.savePct ?? p.discount_pct ?? p.discountPercent ?? p.savingPercent;
		if (typeof pct === 'number' && isFinite(pct) && pct > 0) {
			return Math.round(pct);
		}

		// derive from "was → now" if present
		const was = Number(p.priceWas ?? p.was ?? 0);
		const now = Number(p.price ?? p.min ?? 0);
		if (was > 0 && now > 0 && now < was) {
			return Math.max(1, Math.round((1 - (now / was)) * 100));
		}
		return 0;
	}

	function hasAnyDiscount(p) {
		const pct = p.savePct ?? p.discount_pct ?? p.discountPercent ?? p.savingPercent;
		if (typeof pct === 'number' && pct > 0) {
			return true;
		}
		const was = Number(p.priceWas ?? p.was ?? p.original_ticket_price ?? p.original_face_value ?? 0);
		const price = Number(p.price ?? p.min ?? p.ticket_price ?? 0);
		return was > 0 && price > 0 && price < was;
	}

	const isSpecialOfferPerf = (p) => !isCampaignRow(p) && hasAnyDiscount(p); // includes discounted OLT
	const isExclusivePricePerf = (p) => !isCampaignRow(p) && !isOlt(p) && !hasAnyDiscount(p);

	function userOpenMs() {
		// Check cookie client-side instead of cached eligible flag
		if (hasCampaignCookie() && prioMs !== null) {
			return prioMs;
		}
		return (revealMs !== null) ? revealMs : null;
	}
	function canBookNow() {
		const openAt = userOpenMs();
		if (openAt === null) {
			return false;
		}
		return Date.now() >= openAt;
	}
	function onSaleMsg() {
		const tMs = userOpenMs();
		if (tMs === null) {
			return 'On sale soon';
		}

		const t = new Date(tMs);
		const sameDay =
		fmtLDN(t, { year: 'numeric', month: '2-digit', day: '2-digit' }) ===
		fmtLDN(new Date(), { year: 'numeric', month: '2-digit', day: '2-digit' });

		// Format time as "2pm" instead of "2:00 PM"
		const timeStr = fmtLDN(t, { hour: 'numeric', minute: '2-digit', hour12: true })
			.replace(':00', '') // remove ":00"
			.replace(' ', '') // remove space before am/pm
			.toLowerCase(); // "pm" instead of "PM"

		if (sameDay) {
			return `On sale at ${ timeStr }`;
		}

		const dateStr = fmtLDN(t, { day: 'numeric', month: 'long' });
		return `On sale on ${ dateStr } at ${ timeStr }`;
	}

	function perfTimestamp(p) {
		const iso = p.iso || `${ p.date }T${ (p.time || '00:00') }:00`;
		const ms = Date.parse(iso);
		return Number.isNaN(ms) ? null : ms;
	}
	function isPastPerf(p) {
		const ms = perfTimestamp(p);
		const cutoff = Date.now() + (60 * 60 * 1000);
		return ms !== null && ms < cutoff;
	}

	const isOlt = (p) =>
		(String(p.source || '').toLowerCase() === 'olt') ||
		((p.labels || []).some((l) => /^olt$/i.test(String(l))));

	const isCampaignRow = (p) => {
		if (!campaignBrandActive) {
			return false;
		}
		const labs = (p.labels || []).map((x) => String(x).toLowerCase());
		return labs.some((l) => /kids/.test(l)) ||
         labs.some((l) => /new\s*year'?s?\s*sale/.test(l));
	};

	const isKidsRow = (p) => (p.labels || []).some((l) => /kids/i.test(String(l || '')));

	const bestSavePctFor = (rows) => {
		let best = null;
		rows.forEach((r) => {
			const pct = getSavingPct(r);
			if (pct > 0) {
				best = (best == null) ? pct : Math.max(best, pct);
			}
		});
		return best;
	};

	const escapeHtml = (s) =>
		String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ c ]));

	const slugifyLabel = (s) => String(s)
		.toLowerCase()
		.normalize('NFKD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/&/g, ' and ')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');

	// ---------- labels universe ----------
	const labelSet = new Set();
	perfs.forEach((p) => visibleLabels(p.labels || []).forEach((l) => l && labelSet.add(String(l))));

	const OFFER_LABEL = /^(?:tkts|special\s*offer|exclusive\s*price|offers?|offer)$/i;

	const wantedFirst = ['Kids Week', 'New Year Sale', 'New Years Sale'];

	const foundFirst = wantedFirst

		.map((w) => [...labelSet].find((l) => lower(l) === lower(w)))
		.filter(Boolean);

	const rest = [...labelSet].filter((l) =>

		!foundFirst.some((f) => lower(f) === lower(l)) &&
		lower(l) !== 'olt' &&
		!OFFER_LABEL.test(String(l))

	);

	const labelUniverse = [...new Set([...foundFirst, ...rest])];

	function resolveChipLabel(kind) {
		if (kind === 'otd') {
			return labelUniverse.find((l) => /tkts|on[-\s]?the[-\s]?day|special[-\s]offer/i.test(l)) || null;
		}
		if (kind === 'kw') {
			return labelUniverse.find((l) => /^kids week$/i.test(l)) || null;
		}
		if (kind === 'nys') {
			return labelUniverse.find((l) => /new\s*year'?s?\s*sale/i.test(l)) || null;
		}
		return null;
	}
	function setLabelChecked(label, checked) {
		if (!label) {
			return;
		}
		if (checked) {
			active.labels.add(label);
		} else {
			active.labels.delete(label);
		}
		const cb = filterListEl?.querySelector(`input[type="checkbox"][data-filter="label"][value="${ CSS.escape(label) }"]`);
		if (cb) {
			cb.checked = checked;
		}
	}
	function syncChips() {
		const chips = promoWrap?.querySelectorAll('[data-chip]');
		chips?.forEach((chip) => {
			const kind = chip.getAttribute('data-chip');
			let on = false;
			if (kind === 'otd') {
				const mode = promosPresent.hasOtdDiscount ? 'special' : 'exclusive';
				on = active.offerFilter === mode;
			} else {
				const label = resolveChipLabel(kind);
				on = label ? active.labels.has(label) : false;
			}
			chip.classList.toggle('is-active', on);
			chip.setAttribute('aria-pressed', on ? 'true' : 'false');
		});
	}

	// promos present
	const promosPresent = (() => {
		let hasOtd = false;
		let hasOtdDiscount = false;
		let hasKw = false;
		let hasNys = false;
		for (const p of perfs) {
			if ((p.avail || 0) <= 0) {
				continue;
			}
			const labs = (p.labels || []).map((x) => (x || '').toString().toLowerCase());
			// Any discounted non-campaign row counts as Special Offer.
			// This intentionally includes discounted OLT rows.
			if (!isCampaignRow(p)) {
				if (hasAnyDiscount(p)) {
					hasOtd = true;
					hasOtdDiscount = true;
				} else if (!isOlt(p) && isOfferAllowed(p)) {
				// Non-OLT with no discount = Exclusive Price / rebanded offer.
					hasOtd = true;
				}
			}
			if (campaignBrandActive && labs.some((l) => /kids/.test(l))) {
				hasKw = true;
			}
			if (campaignBrandActive && labs.some((l) => /new\s*year'?s?\s*sale/.test(l))) {
				hasNys = true;
			}
		}
		return { hasOtd, hasOtdDiscount, hasKw, hasNys };
	})();

	// sessions available
	const hasMatinee = perfs.some((p) => sessionOf(p.time) === 'Matinee');
	const hasEvening = perfs.some((p) => sessionOf(p.time) === 'Evening');

	// ---------- filter state ----------
	const active = { labels: new Set(), sessions: new Set(), offerFilter: null };
	const matchFilters = (perf) => {
		// Promotions chip takes precedence and is not label-based
		if (active.offerFilter === 'special' && !isSpecialOfferPerf(perf)) {
			return false;
		}
		if (active.offerFilter === 'exclusive' && !isExclusivePricePerf(perf)) {
			return false;
		}

		if (active.labels.size) {
			const labels = (perf.labels || []).map(String);
			if (!labels.some((l) => active.labels.has(l))) {
				return false;
			}
		}
		if (active.sessions.size && !active.sessions.has(sessionOf(perf.time))) {
			return false;
		}
		return true;
	};
	const perfsOnDate = (key) => perfs.filter((p) => p.date === key).filter((p) => !isPastPerf(p)).filter(isOfferAllowed).filter(matchFilters);

	function jumpToFirstMatchingMonthForActiveFilters() {
		// Find all future-allowed perfs that pass the current filters
		const candidates = perfs.filter((p) => {
			if ((p.avail || 0) <= 0) {
				return false;
			}
			if (isPastPerf(p)) {
				return false;
			}
			if (!isOfferAllowed(p)) {
				return false;
			}
			return matchFilters(p);
		});

		if (!candidates.length) {
			return; // nothing to jump to
		}

		// Sort by date + time so we get the earliest one
		candidates.sort((a, b) => {
			const aKey = `${ a.date || '' }T${ a.time || '' }`;
			const bKey = `${ b.date || '' }T${ b.time || '' }`;
			return aKey.localeCompare(bKey);
		});

		const first = candidates[ 0 ];
		const d = parseYMD(first.date);
		if (!d) {
			return;
		}

		viewYear = d.getFullYear();
		viewMonth = d.getMonth();
	}

	// promos row
	function buildPromos() {
		if (!promoWrap) {
			return;
		}

		const parts = [];
		if (promosPresent.hasOtd && !hideDiscountMessaging) {
			const chipText = promosPresent.hasOtdDiscount ? 'Special Offer' : 'Exclusive Price';
			const isOn = active.offerFilter === (promosPresent.hasOtdDiscount ? 'special' : 'exclusive');

			parts.push(
				`<button type="button" class="chip chip--otd ${ isOn ? 'is-active' : '' }" data-chip="otd" aria-pressed="${ isOn ? 'true' : 'false' }">
         <span class="chip__dot chip__dot--otd"></span>${ chipText }
       </button>`
			);
		}
		if (promosPresent.hasKw) {
			const on = active.labels.has(resolveChipLabel('kw') || 'Kids Week');
			parts.push(
				`<button type="button" class="chip chip--kw ${ on ? 'is-active' : '' }" data-chip="kw" aria-pressed="${ on ? 'true' : 'false' }">
         <span class="chip__dot chip__dot--kw"></span>${ resolveChipLabel('kw') || 'Kids Week' }
       </button>`
			);
		}
		if (promosPresent.hasNys) {
			const on = active.labels.has(resolveChipLabel('nys') || 'New Year Sale');
			parts.push(
				`<button type="button" class="chip chip--nys ${ on ? 'is-active' : '' }" data-chip="nys" aria-pressed="${ on ? 'true' : 'false' }">
         <span class="chip__dot chip__dot--nys"></span>${ resolveChipLabel('nys') || 'New Year Sale' }
       </button>`
			);
		}

		if (!parts.length) {
			promoWrap.remove(); return;
		}

		promoWrap.hidden = false;
		promoWrap.removeAttribute('aria-hidden');
		promoWrap.innerHTML = parts.join('');

		promoWrap.addEventListener('click', (e) => {
			const btn = e.target.closest('[data-chip]');
			if (!btn) {
				return;
			}
			const kind = btn.getAttribute('data-chip');

			if (kind === 'otd') {
				// Toggle our dedicated offer filter
				const targetMode = promosPresent.hasOtdDiscount ? 'special' : 'exclusive';
				const willTurnOn = (active.offerFilter !== targetMode);

				active.offerFilter = willTurnOn ? targetMode : null;
				syncChips();

				if (willTurnOn) {
					// Jump to first month that actually has Special/Exclusive offers
					jumpToFirstMatchingMonthForActiveFilters();
				}

				render();
				return;
			}

			// existing behaviour for KW/NYS (label-based)
			const label = resolveChipLabel(kind);
			if (!label) {
				return;
			}

			const willTurnOn = !active.labels.has(label);
			setLabelChecked(label, willTurnOn);
			syncChips();

			if (willTurnOn) {
				// Jump to first month that has Kids Week / New Year Sale dates
				jumpToFirstMatchingMonthForActiveFilters();
			}

			render();
		});
	}
	// label filters
	function buildLabelFilters() {
		if (!filterListEl) {
			return;
		}
		filterListEl.innerHTML = labelUniverse.map((l) => `
            <label class="farlo-ui-ticket-calendar__filter-item">
                <input type="checkbox" value="${ String(l).replace(/"/g, '&quot;') }" data-filter="label">
                <span class="farlo-ui-ticket-calendar__filter-chip">${ l }</span>
            </label>`).join('');
		filterListEl.addEventListener('change', (e) => {
			const el = e.target; if (!el || el.tagName !== 'INPUT') {
				return;
			}
			if (el.dataset.filter === 'label') {
				el.checked ? active.labels.add(el.value) : active.labels.delete(el.value); render();
			}
		});
	}

	// session chips
	const sessionsMarkup = ({ matineeChecked = false, eveningChecked = false } = {}) => [
		hasMatinee ? `<label class="farlo-ui-ticket-calendar__filter-item">
            <input type="checkbox" value="Matinee" data-filter="session" ${ matineeChecked ? 'checked' : '' }>
            <span class="farlo-ui-ticket-calendar__filter-chip">Matinee</span></label>` : '',
		hasEvening ? `<label class="farlo-ui-ticket-calendar__filter-item">
            <input type="checkbox" value="Evening" data-filter="session" ${ eveningChecked ? 'checked' : '' }>
            <span class="farlo-ui-ticket-calendar__filter-chip">Evening</span></label>` : ''
	].join('');
	function syncSessionChecks() {
		const cm = active.sessions.has('Matinee'),
			ce = active.sessions.has('Evening');
		if (filterTimesInline) {
			filterTimesInline.innerHTML = sessionsMarkup({ matineeChecked: cm, eveningChecked: ce });
		}
		if (filterTimesPop) {
			filterTimesPop.innerHTML = sessionsMarkup({ matineeChecked: cm, eveningChecked: ce });
		}
	}
	function bindSessionHandlers(container) {
		if (!container) {
			return;
		}
		container.addEventListener('change', (e) => {
			const el = e.target; if (!el || el.tagName !== 'INPUT' || el.dataset.filter !== 'session') {
				return;
			}
			el.checked ? active.sessions.add(el.value) : active.sessions.delete(el.value);
			syncSessionChecks(); render();
		});
	}

	// bounds
	const dateKeysAll = [...new Set(perfs.map((p) => p.date))].sort();
	const minDate = dateKeysAll[ 0 ] ? parseYMD(dateKeysAll[ 0 ]) : new Date();
	const maxDate = dateKeysAll.length ? parseYMD(dateKeysAll[ dateKeysAll.length - 1 ]) : new Date();
	let viewYear = minDate.getFullYear(),
		viewMonth = minDate.getMonth();

	const urlDate = new URLSearchParams(window.location.search).get('date');
	const isValidYmd = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s);

	if (isValidYmd(urlDate)) {
		const d = parseYMD(urlDate);
		// Clamp into [minDate, maxDate] bounds, then set view to that month
		if (d && (!minDate || d >= minDate) && (!maxDate || d <= maxDate)) {
			viewYear = d.getFullYear();
			viewMonth = d.getMonth();
		}
	}

	function updateNavState() {
		const curr = viewYear * 12 + viewMonth,
			minI = minDate.getFullYear() * 12 + minDate.getMonth(),
			maxI = maxDate.getFullYear() * 12 + maxDate.getMonth();
		const atMin = curr <= minI,
			atMax = curr >= maxI;
		prevBtn.disabled = atMin; prevBtn.classList.toggle('is-disabled', atMin); prevBtn.setAttribute('aria-disabled', atMin ? 'true' : 'false');
		nextBtn.disabled = atMax; nextBtn.classList.toggle('is-disabled', atMax); nextBtn.setAttribute('aria-disabled', atMax ? 'true' : 'false');
	}

	// render helpers
	const badgeHTML = (l) => {
		const txt = escapeHtml(l);
		const mod = slugifyLabel(l);
		return `<span class="farlo-ui-ticket-calendar__badge farlo-ui-ticket-calendar__badge--${ mod }">${ txt }</span>`;
	};

	function priceAndCtaHTML(p, { isOffer = false, cheapest = 0 } = {}) {
		if (isCampaignRow(p) && !canBookNow()) {
			return `<div class="farlo-ui-ticket-calendar__from farlo-ui-ticket-calendar__from--locked">${ onSaleMsg() }</div>`;
		}

		const pMin = (p.min !== null && p.min !== undefined) ? Number(p.min) : null;
		// Apply the parts multiplier. Kids Week adds an additional x2 for the
		// mandatory adult + child pair.
		const displayMin = pMin !== null ? pMin * partsCount * (isKidsRow(p) ? 2 : 1) : null;
		const price = displayMin !== null ? formatMoney(displayMin) : '';
		const href = p.bookUrl || '#';
		const isCheapest = (pMin !== null && cheapest > 0 && Math.abs(pMin - cheapest) < 0.01);
		const disc = p.savePct ?? p.discount_pct ?? p.discountPercent ?? p.savingPercent ?? 0;
		const dataAttrs = `data-event="click_price" data-id="${ payload.showId || '' }" data-name="${ escapeHtml(payload.showName || '') }" data-offer="${ isOffer }" data-cheapest="${ isCheapest }" data-offer-perc="${ isOffer ? (parseInt(disc, 10) || 0) : 0 }" data-price="${ pMin !== null ? pMin.toFixed(2) : '' }" data-source="${ escapeHtml(p.source || '') }"`;
		return `${ price ? `<div class="farlo-ui-ticket-calendar__from">${ isCampaignRow(p) ? (isKidsRow(p) ? 'Adult + Child' : 'Exclusive prices') : '' } from <strong>${ price }</strong></div>` : '' }
          <a class="farlo-ui-ticket-calendar__book button button--book" href="${ href }" ${ dataAttrs }>Book Tickets</a>`;
	}

	function timeSlotLine(time, perfsForThisTime, date, tag = 'div') {
		const representativePerf = perfsForThisTime[ 0 ];
		const timeISO = representativePerf.iso || `${ date }T${ time }:00`;

		const CLEAN_BADGE = /^(?:special\s*offer|exclusive\s*price|on[-\s]?the[-\s]?day\s*tickets?|offers?)$/i;
		const allLabels = new Set();
		perfsForThisTime.forEach((p) => {
			visibleLabels(p.labels || [])
				.filter((l) => !CLEAN_BADGE.test(String(l || '')))
				.forEach((l) => allLabels.add(l));
		});

		// Derive ONE badge for the slot:
		// Special if ANY discounted (OLT or non-OLT) non-campaign rows;
		// else Exclusive if any allowed non-OLT, non-campaign rows exist
		const anyDiscount = perfsForThisTime.some((p) => !isCampaignRow(p) && hasAnyDiscount(p));
		const anyExclusive = !anyDiscount && perfsForThisTime.some((p) => !isCampaignRow(p) && !isOlt(p) && isOfferAllowed(p));
		const derivedBadge = hideDiscountMessaging ? '' : (anyDiscount ? 'Special Offer' : (anyExclusive ? 'Exclusive Price' : ''));
		if (derivedBadge) {
			allLabels.add(derivedBadge);
		}

		const labelsHtml = [...allLabels].map(badgeHTML).join('');

		// Derive the displayed mins from the FILTERED perfs so the day-card matches
		// what clicking through will actually offer. Without this, an active filter
		// (e.g. Kids Week) that strips a slot's non-KW perf would still surface the
		// non-KW "from £X" line via stale precomputed state.
		let kidsMin = null;
		let otherMin = null;
		perfsForThisTime.forEach((p) => {
			if (p.min == null) {
				return;
			}
			const v = Number(p.min);
			const isKidsPerf = (p.labels || []).some((l) => /kids/i.test(l || ''));
			if (isKidsPerf) {
				kidsMin = kidsMin === null ? v : Math.min(kidsMin, v);
			} else {
				otherMin = otherMin === null ? v : Math.min(otherMin, v);
			}
		});
		const isKids = kidsMin !== null;
		// Kids Week: one adult + one child ticket per part.
		// All other sources: one ticket per part.
		const kidsMinStr = (kidsMin !== null) ? formatMoney(kidsMin * partsCount * 2) : '';
		const otherMinStr = (otherMin !== null) ? formatMoney(otherMin * partsCount) : '';

		const minsHtml = isKids
			? `${ kidsMinStr ? `<div class="farlo-ui-ticket-calendar__from-line farlo-ui-ticket-calendar__from-line--kw">Adult + Child from <strong>${ kidsMinStr }</strong></div>` : '' }
               ${ otherMinStr ? `<div class="farlo-ui-ticket-calendar__from-line">from <strong>${ otherMinStr }</strong></div>` : '' }`
			: (otherMinStr ? `<div class="farlo-ui-ticket-calendar__from-line">from <strong>${ otherMinStr }</strong></div>` : '');

		const content = `
            <div class="farlo-ui-ticket-calendar__slot-head">
                <div class="farlo-ui-ticket-calendar__badges">${ labelsHtml }</div>
                <span class="farlo-ui-ticket-calendar__slot-time-group">
                    <time class="farlo-ui-ticket-calendar__time">${ toTimeLabel(timeISO) }</time>${ accessIconsForSlot(date, time) }
                </span>
            </div>
            <div class="farlo-ui-ticket-calendar__slot-meta">${ minsHtml }</div>`;

		const uniqueUrlsForTime = [...new Set(perfsForThisTime.map((p) => p.bookUrl).filter(Boolean))];
		const finalTag = (tag === 'a' && uniqueUrlsForTime.length === 1) ? 'a' : 'div';
		const hrefAttr = (finalTag === 'a') ? `href="${ uniqueUrlsForTime[ 0 ] }"` : '';

		return `<${ finalTag } class="farlo-ui-ticket-calendar__slot" ${ hrefAttr }>${ content }</${ finalTag }>`;
	}

	/**
	 * Builds the shared access icon container markup for a collection of access
	 * performance type slugs. Slugs without an ACCESS_ICON_MAP entry are skipped.
	 *
	 * @param {Set<string>|string[]} slugs - Access performance type slugs.
	 * @return {string} Icon container HTML, or an empty string when nothing renders.
	 */
	function renderAccessIcons(slugs) {
		const icons = [...slugs].map((slug) => {
			const info = ACCESS_ICON_MAP[ slug ];

			if (!info) {
				return '';
			}

			return `<span class="farlo-ui-ticket-calendar__access-icon farlo-ui-ticket-calendar__access-icon--${ info.mod }" role="img" aria-label="${ escapeHtml(info.label) }"><span class="farlo-ui-ticket-calendar__access-icon-label">${ escapeHtml(info.label) }</span></span>`;
		}).filter(Boolean).join('');

		if (!icons) {
			return '';
		}

		return `<span class="farlo-ui-ticket-calendar__access-icons" aria-label="Access performance">${ icons }</span>`;
	}

	/**
	 * Returns an HTML string of access performance type icons for a given calendar
	 * date key. Aggregates all type slugs across every time key for that date.
	 * Used only for otherwise-empty days that have no ticketing performances.
	 *
	 * @param {string} key - YYYY-MM-DD date string.
	 * @return {string}
	 */
	function accessIconsHTML(key) {
		const dateEntry = accessDates[ key ];

		if (!dateEntry || typeof dateEntry !== 'object') {
			return '';
		}

		// Collect unique slugs across all time keys ('HH:MM' or 'any').
		const allSlugs = new Set();
		Object.values(dateEntry).forEach((slugs) => slugs.forEach((s) => allSlugs.add(s)));

		if (!allSlugs.size) {
			return '';
		}

		return renderAccessIcons(allSlugs);
	}

	/**
	 * Returns an HTML string of access performance type icons for a specific date
	 * and time slot. Checks both the exact time key and the 'any' key so that
	 * access performances recorded without a time still appear on every slot.
	 *
	 * @param {string} date - YYYY-MM-DD date string.
	 * @param {string} time - HH:MM 24-hour time string (from performance data).
	 * @return {string}
	 */
	function accessIconsForSlot(date, time) {
		const dateEntry = accessDates[ date ];

		if (!dateEntry || typeof dateEntry !== 'object') {
			return '';
		}

		const types = new Set([
			...(dateEntry[ time ] || []),
			...(dateEntry.any || [])
		]);

		if (!types.size) {
			return '';
		}

		return renderAccessIcons(types);
	}

	function cellHTML(y, m, d) {
		const key = dateKey(y, m, d);
		const todays = perfsOnDate(key);
		const accessHtml = accessIconsHTML(key);

		if (!todays.length) {
			return `<div class="farlo-ui-ticket-calendar__day farlo-ui-ticket-calendar__day--empty${ accessHtml ? ' farlo-ui-ticket-calendar__day--access' : '' }"><span class="farlo-ui-ticket-calendar__day-number">${ d }</span>${ accessHtml }</div>`;
		}

		const hasPromo = todays.some((p) => (!isCampaignRow(p) && hasAnyDiscount(p))) || todays.some((p) => {
			const labs = visibleLabels(p.labels || []);
			return labs.length > 0 && !labs.every((l) => lower(l) === 'olt');
		});

		const uniqueTimes = [...new Set(todays.map((p) => p.time))].sort();
		const top2Times = uniqueTimes.slice(0, 2);
		const more = uniqueTimes.length > 2;

		const slotTag = hasPromo ? 'div' : 'a';

		// When the outer cell is itself a <button> (hasPromo), the "More times"
		// element MUST NOT be a nested <button>. HTML disallows nested buttons and
		// the parser hoists the inner one out as a sibling, shifting every later
		// day by one column. Use a <span> in that case; the click bubbles to the
		// outer cell-button (delegated via closest('button[data-date]')), so the
		// modal still opens. When the cell is a <div>, keep the <button> so the
		// element itself is the click target.
		const moreHtml = more
			? (hasPromo
				? `<span class="farlo-ui-ticket-calendar__more">More times</span>`
				: `<button type="button" class="farlo-ui-ticket-calendar__more" data-date="${ key }">More times</button>`)
			: '';

		const bodyHtml = top2Times.map((time) => {
			const perfsForThisTime = todays.filter((p) => p.time === time);
			return timeSlotLine(time, perfsForThisTime, key, slotTag);
		}).join('') + moreHtml;

		if (hasPromo) {
			return `
                <button class="farlo-ui-ticket-calendar__cell" type="button" data-date="${ key }">
                    <span class="farlo-ui-ticket-calendar__cell-number">${ d }</span>
                    <div class="farlo-ui-ticket-calendar__cell-body">${ bodyHtml }</div>
                </button>`;
		}
		return `
                <div class="farlo-ui-ticket-calendar__cell">
                    <span class="farlo-ui-ticket-calendar__cell-number">${ d }</span>
                    <div class="farlo-ui-ticket-calendar__cell-body">${ bodyHtml }</div>
                </div>`;
	}

	function renderGrid(y, m) {
		headerEl.textContent = new Date(Date.UTC(y, m, 1)).toLocaleString(undefined, { month: 'long', year: 'numeric', timeZone: LDN_TZ });
		daysRoot.innerHTML = '';
		const first = firstDow(y, m),
			days = daysInMon(y, m);
		for (let i = 0; i < first; i ++) {
			daysRoot.insertAdjacentHTML('beforeend', '<div class="farlo-ui-ticket-calendar__day farlo-ui-ticket-calendar__day--empty"></div>');
		}
		for (let d = 1; d <= days; d ++) {
			daysRoot.insertAdjacentHTML('beforeend', cellHTML(y, m, d));
		}
		updateNavState();
	}

	function renderList(y, m) {
		headerEl.textContent = new Date(Date.UTC(y, m, 1)).toLocaleString(undefined, { month: 'long', year: 'numeric', timeZone: LDN_TZ });
		listRoot.innerHTML = '';
		const days = daysInMon(y, m);

		for (let d = 1; d <= days; d ++) {
			const key = dateKey(y, m, d);
			const todays = perfsOnDate(key);
			if (!todays.length) {
				continue;
			} // nothing left (all past) → skip day

			const header = new Date(y, m, d).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
			const sectionsHtml = renderGroupedSectionsForDay(todays, {
				showSpecialOfferNote: specialOfferNote,
				showSavePct: true
			});

			listRoot.insertAdjacentHTML('beforeend', `
      <li class="farlo-ui-ticket-calendar__list-group">
        <h3 class="farlo-ui-ticket-calendar__list-date">${ header }</h3>
        <div class="farlo-ui-ticket-calendar__list-group-body">
          ${ sectionsHtml || '<p class="farlo-ui-ticket-calendar__empty">No performances matching your filters.</p>' }
        </div>
      </li>
    `);
		}
		updateNavState();
	}

	// modal
	const modal = document.querySelector('[data-cal-modal]');
	const modalTitle = document.querySelector('[data-cal-modal-title]');
	const modalContent = document.querySelector('[data-cal-modal-content]');
	const modalClose = document.querySelectorAll('[data-cal-close]');

	function renderGroupedSectionsForDay(rows, { showSpecialOfferNote = '', showSavePct = true } = {}) {
		const timeHtml = (p, show) => {
			const iso = p.iso || `${ p.date }T${ p.time }:00`;
			return show
				? `<time class="farlo-ui-ticket-calendar__time">${ toTimeLabel(iso) }</time>`
				: '<span class="farlo-ui-ticket-calendar__time farlo-ui-ticket-calendar__time--placeholder" aria-hidden="true"></span>';
		};

		const otherSeatsBadgeHtml = (show) =>
			show
				? `<span class="farlo-ui-ticket-calendar__badge farlo-ui-ticket-calendar__badge--other-seats">View other available seats</span>`
				: '';

		// --- helpers for this renderer ---
		const isValidPrice = (v) => typeof v === 'number' && isFinite(v) && v > 0;
		const betterOf = (a, b) => {
			// prefer OLT; else lower min; else keep current
			const aIsOlt = isOlt(a),
				bIsOlt = isOlt(b);
			if (bIsOlt && !aIsOlt) {
				return b;
			}
			if (aIsOlt && !bIsOlt) {
				return a;
			}
			const aMin = isValidPrice(a.min) ? a.min : Infinity;
			const bMin = isValidPrice(b.min) ? b.min : Infinity;
			return (bMin < aMin) ? b : a;
		};
		const dedupeByTime = (arr) => {
			const map = new Map();
			arr.forEach((p) => {
				const k = p.time; // same time within a day
				map.set(k, map.has(k) ? betterOf(map.get(k), p) : p);
			});
			return [...map.values()];
		};

		// ---- partition rows ----
		const nonCampaign = rows.filter((p) => !isCampaignRow(p));

		const oltRows = nonCampaign.filter(isOlt);
		const discRows = nonCampaign.filter(hasAnyDiscount).filter(isOfferAllowed); // includes discounted OLT
		const fullOlt = oltRows.filter((p) => !hasAnyDiscount(p)); // OLT no discount
		const undiscNonOlt = nonCampaign.filter((p) => !isOlt(p) && !hasAnyDiscount(p)).filter(isOfferAllowed);

		// Decide what to show
		let offerRows, fullRows;
		if (discRows.length) {
			// SPECIAL OFFER: only discounted rows (OLT and/or non-OLT), de-duped by time
			offerRows = dedupeByTime(discRows);
			fullRows = fullOlt; // still show non-discounted OLT in "Full"
		} else {
			// EXCLUSIVE: no discounts show re-banded non-OLT
			offerRows = dedupeByTime(undiscNonOlt);
			fullRows = fullOlt;
		}

		const kwRows = rows.filter((r) => (r.labels || []).some((l) => /kids/i.test(String(l))));
		const nysRows = rows.filter((r) => (r.labels || []).some((l) => /new\s*year'?s?\s*sale/i.test(String(l))));

		const offerBestPct = bestSavePctFor(offerRows);
		const offerTitle = hideDiscountMessaging
			? ''
			: ((offerRows.length && (!offerBestPct || offerBestPct <= 0))
				? 'Exclusive Price'
				: 'Special Offer');
		const offerNote = hideDiscountMessaging ? '' : (offerTitle === 'Exclusive Price' ? exclusiveOfferNote : specialOfferNote);

		// Cheapest price across all options for analytics
		const _allMins = [...offerRows, ...fullRows].map((p) => Number(p.min)).filter((v) => v > 0);
		const _cheapest = _allMins.length ? Math.min(..._allMins) : 0;

		const renderSection = (title, sectionRows, { mod = '', showPct = false, note = '', showOtherSeatsBadge = false } = {}) => {
			if (!sectionRows.length) {
				return '';
			}
			const bestPct = showPct ? bestSavePctFor(sectionRows) : null;
			const sectionIsOffer = mod === 'farlo-ui-ticket-calendar__section--offer';

			const byTime = sectionRows.reduce((acc, p) => {
				(acc[ p.time ] ||= []).push(p);
				return acc;
			}, {});

			// after dedupeByTime() each time will have 1 row, but keep the loop robust
			const listHtml = Object.values(byTime).map((group) =>
				group.slice(0, 1).map((p, i) => `
        <div class="farlo-ui-ticket-calendar__option-row${ sectionIsOffer ? ' farlo-ui-ticket-calendar__option-row--offer' : '' }">
          <div class="farlo-ui-ticket-calendar__option-left">
            <span class="farlo-ui-ticket-calendar__slot-time-group">
              ${ timeHtml(p, i === 0) }${ i === 0 ? accessIconsForSlot(p.date, p.time) : '' }
            </span>
            ${ (showPct && bestPct && !hideDiscountMessaging) ? `<span class="farlo-ui-ticket-calendar__badge farlo-ui-ticket-calendar__badge--save">up to ${ bestPct }% off</span>` : '' }
            ${ (!showPct && showOtherSeatsBadge && i === 0) ? otherSeatsBadgeHtml(true) : '' }
          </div>
          <div class="farlo-ui-ticket-calendar__option-right">
            ${ priceAndCtaHTML(p, { isOffer: sectionIsOffer, cheapest: _cheapest }) }
          </div>
        </div>
      `).join('')
			).join('');

			return `
      <div class="farlo-ui-ticket-calendar__section ${ mod }">
        ${ (title && String(title).trim()) ? `<h4 class="farlo-ui-ticket-calendar__section-title">${ title }</h4>` : '' }
        ${ listHtml }
        ${ note ? `<p class="farlo-ui-ticket-calendar__offer-note">${ note }</p>` : '' }
      </div>
    `;
		};

		return (
			renderSection('Kids Week', kwRows, { mod: 'farlo-ui-ticket-calendar__section--kw' }) +
			renderSection('New Year Sale', nysRows, { mod: 'farlo-ui-ticket-calendar__section--nys' }) +
			renderSection(offerTitle, offerRows, { mod: 'farlo-ui-ticket-calendar__section--offer', showPct: showSavePct, note: offerNote }) +
			renderSection('', fullRows, { mod: 'farlo-ui-ticket-calendar__section--full', showOtherSeatsBadge: true })
		);
	}
	function openModalFor(key) {
		const todays = perfsOnDate(key);
		if (!todays.length || !modal) {
			return;
		}

		const d = parseYMD(key);

		if (modalTitle) {
			const w = d.toLocaleString(undefined, { weekday: 'short' });
			const day = d.toLocaleString(undefined, { day: 'numeric' });
			// Some en-GB browsers return "Sept" for a short month. Keep popup
			// headings consistent with the three-letter date labels used elsewhere.
			const month = d.toLocaleString(undefined, { month: 'short' }).slice(0, 3);
			modalTitle.innerHTML = `${ w } ${ day } ${ month }`;
		}

		const html = renderGroupedSectionsForDay(todays, {
			showSpecialOfferNote: specialOfferNote,
			showSavePct: true
		});
		modalContent.innerHTML = html;
		modal.hidden = false;
		document.documentElement.classList.add('is-modal-open');
	}
	function closeModal() {
		if (!modal) {
			return;
		} modal.hidden = true; document.documentElement.classList.remove('is-modal-open');
	}
	modalClose.forEach((btn) => btn.addEventListener('click', closeModal));
	modal?.addEventListener('click', (e) => {
		if (e.target === modal) {
			closeModal();
		}
	});

	// interactions
	daysRoot.addEventListener('click', (e) => {
		const trigger = e.target.closest('button[data-date]');
		if (trigger) {
			e.preventDefault();
			openModalFor(trigger.dataset.date);
		}
	});
	prevBtn.addEventListener('click', () => {
		if (prevBtn.disabled) {
			return;
		} viewMonth--; if (viewMonth < 0) {
			viewMonth = 11; viewYear--;
		}
		render();
	});
	nextBtn.addEventListener('click', () => {
		if (nextBtn.disabled) {
			return;
		} viewMonth ++; if (viewMonth > 11) {
			viewMonth = 0; viewYear ++;
		}
		render();
	});

	listToggle.addEventListener('click', () => {
		const grid = root.querySelector('[data-view="grid"]');
		const list = root.querySelector('[data-view="list"]');
		const toList = list.hasAttribute('hidden');
		listToggle.setAttribute('aria-pressed', toList ? 'true' : 'false');
		list.hidden = !toList; grid.hidden = toList;
		listToggle.textContent = toList ? 'Month View' : 'List View';
		render();
	});

	function render() {
		if (isListView()) {
			renderList(viewYear, viewMonth);
		} else {
			renderGrid(viewYear, viewMonth);
		}
	}

	// init
	buildPromos();
	buildLabelFilters();
	syncSessionChecks();
	bindSessionHandlers(filterTimesInline);
	bindSessionHandlers(filterTimesPop);
	render();
}
